import {TransactionRange} from "../../../../domain/transaction-range"
import {getBeginOfMonth, getWithAddedDay, getWithAddedMonths, toOnlyDateString} from "../../../../util/date-utils"
import {getMonthHeaderWithYearForDate} from "../months"

export interface ChartScopeSettings {
  range: ChartRange
  period: ChartScopePeriod
}

export type PredefinedChartRange = "lastYear" | "lastThreeMonths" | "lastSixMonths" | "all"
export const displayPredefinedChartRange: Record<PredefinedChartRange, string> = {
  "lastYear": "laatste jaar",
  "lastThreeMonths": "laatste 3 maanden",
  "lastSixMonths": "laatste 6 maanden",
  "all" : "alle transacties"
}
export const monthCountForChartRange: Record<PredefinedChartRange, number> = {
  "lastYear": 12,
  "lastThreeMonths": 3,
  "lastSixMonths": 6,
  "all" : 0
}

export interface MonthChartRange {
  monthStart: Date
}
export interface CustomChartRange {
  dateFrom?: Date
  dateUntil?: Date
}
export type ChartRange = PredefinedChartRange | MonthChartRange | CustomChartRange

export type ChartScopePeriod = "month" | "year"
export const displayPeriod: Record<ChartScopePeriod, string> = {
  "month": "maand",
  "year": "jaar"
}

export const getChartScopeDisplayValue = (chartScopeSettings: ChartScopeSettings): string => {
  if (isPredefinedChartRange(chartScopeSettings.range)) {
    return displayPredefinedChartRange[chartScopeSettings.range as PredefinedChartRange]
  }

  if (isMonthChartRange(chartScopeSettings.range)) {
    return getMonthHeaderWithYearForDate((chartScopeSettings.range as MonthChartRange).monthStart)
  }

  if (!chartScopeSettings.range.dateFrom && !chartScopeSettings.range.dateUntil) return displayPredefinedChartRange["all"]
  else if (chartScopeSettings.range.dateFrom && !chartScopeSettings.range.dateUntil) return `vanaf ${toOnlyDateString(chartScopeSettings.range.dateFrom)}`
  else if (!chartScopeSettings.range.dateFrom && chartScopeSettings.range.dateUntil) return `tot ${toOnlyDateString(chartScopeSettings.range.dateUntil)}`
  return `van ${toOnlyDateString(chartScopeSettings.range.dateFrom!)} tot ${toOnlyDateString(chartScopeSettings.range.dateUntil!)}`
}

export const getDateRange = (range: ChartRange, transactionRange: TransactionRange | null): [Date | null, Date | null] => {
  if (transactionRange === null) return [null, null]

  if (isPredefinedChartRange(range)) {
    const fullMonthRange = getFullMonthRange(transactionRange)
    if (fullMonthRange === null) return [null, null]
    const [begin, end] = fullMonthRange

    if (range === "lastYear" || range === "lastThreeMonths" || range === "lastSixMonths") {
      const idealBegin = getWithAddedMonths(end, -monthCountForChartRange[range])
      return [idealBegin >= begin ? idealBegin : begin, end]
    }

    if (range === "all") {
      return [getBeginOfMonth(transactionRange.first), getWithAddedMonths(getBeginOfMonth(transactionRange.last), 1)]
    }

    throw new Error("Unexpected chart range found: " + range)
  }

  if (isMonthChartRange(range)) {
    const monthStart = (range as MonthChartRange).monthStart
    return [monthStart, getWithAddedMonths(monthStart, 1)]
  }

  return [(range as CustomChartRange).dateFrom ?? null, (range as CustomChartRange).dateUntil ?? null]
}


export function isPredefinedChartRange(range: ChartRange): range is PredefinedChartRange {
  return ["lastYear", "lastThreeMonths", "lastSixMonths", "currentMonth"].includes(range as PredefinedChartRange)
}

export function isMonthChartRange(range: ChartRange): range is MonthChartRange {
  return (range as MonthChartRange).monthStart !== undefined
}


export const getFullMonthRange = (transactionRange: TransactionRange) => {
  const first = new Date(transactionRange.first)
  const last = new Date(transactionRange.last)

  const firstFull = getBeginOfMonth(first)
  if ((first.getDate() > 1 && first.getMonth() > 0) || first.getDate() > 2) firstFull.setMonth(firstFull.getMonth() + 1) // first is not the start of the month, so use the next month (except January - excuse no transactions on 1st of January)

  const lastFull = getBeginOfMonth(getWithAddedDay(last))

  if (lastFull <= firstFull) return null // no full months
  return [firstFull, lastFull]
}